import { useTranslation } from 'next-i18next';
import React from 'react';
import { BsIcon } from '@hotelplan/core.basis.bs-icon';
import { FdrImage } from '@hotelplan/fdr.regular.basis.fdr-image';
import { useDeviceType } from '@hotelplan/libs.context.device-type';
import { useLazyFetchingPagination } from '@hotelplan/libs.hooks';
import { FdrPagination } from '@hotelplan/supergraph-api';
import {
  trackDeleteBookmarkGroup,
  trackEditBookmarkGroup,
} from 'components/domain/tracking/bookmarkTracking';
import {
  BookmarkModalPopupType,
  useBookmarkModalPopupContext,
} from './context/fdr-bookmark-modal-popup-context';
import { IBookmarkGroupState } from './fdr-bookmark-types';
import {
  BookmarkGroupItemButtons,
  BookmarkGroupItemCount,
  BookmarkGroupItemImage,
  BookmarkGroupItemInfo,
  BookmarkGroupItemLink,
  BookmarkGroupItemTitle,
  BookmarkGroupSlider,
} from './fdr-bookmark.styles';
import useBookmarkGroupData from './hooks/useBookmarkGroupData';

interface IBookmarkGroupItemProps {
  bookmarkGroup: IBookmarkGroupState;
  active: boolean;
  onSelect(bookmarkGroup: IBookmarkGroupState): void;
}

const BookmarkGroupItem: React.FC<IBookmarkGroupItemProps> = ({
  bookmarkGroup,
  active,
  onSelect,
}) => {
  const [, { language }] = useTranslation();
  const bookmarkPopupContext = useBookmarkModalPopupContext();
  const { image } = useBookmarkGroupData(bookmarkGroup.id);

  return (
    <BookmarkGroupItemLink
      active={active}
      onClick={event => {
        event.preventDefault();
        onSelect(bookmarkGroup);
      }}
    >
      <BookmarkGroupItemImage className={'bookmark-group-item-image'}>
        {image && (
          <div>
            <FdrImage image={image} />
          </div>
        )}
      </BookmarkGroupItemImage>
      <BookmarkGroupItemInfo>
        <BookmarkGroupItemTitle>{bookmarkGroup.name}</BookmarkGroupItemTitle>
        <BookmarkGroupItemCount>
          {bookmarkGroup.items.length}
        </BookmarkGroupItemCount>
      </BookmarkGroupItemInfo>
      <BookmarkGroupItemButtons>
        <button
          onClick={e => {
            e.preventDefault();
            trackEditBookmarkGroup(language);

            bookmarkPopupContext.open({
              type: BookmarkModalPopupType.EDIT_BOOKMARK_GROUP,
              payload: {
                groupId: bookmarkGroup.id,
                name: bookmarkGroup.name,
              },
            });
          }}
        >
          <BsIcon name="bookmark-edit" className="bookmark-edit" />
        </button>
        <button
          onClick={e => {
            e.preventDefault();
            trackDeleteBookmarkGroup(language);

            bookmarkPopupContext.open({
              type: BookmarkModalPopupType.REMOVE_BOOKMARK_GROUP,
              payload: {
                groupId: bookmarkGroup.id,
                name: bookmarkGroup.name,
              },
            });
          }}
        >
          <BsIcon name="delete" className="bookmark-remove" />
        </button>
      </BookmarkGroupItemButtons>
    </BookmarkGroupItemLink>
  );
};

interface IBookmarkGroupListProps {
  bookmarkGroups: IBookmarkGroupState[];
  activeGroup: IBookmarkGroupState;
  page: FdrPagination;
  onSelect(bookmarkGroup: IBookmarkGroupState): void;
}

const FdrBookmarkGroupList: React.FC<IBookmarkGroupListProps> = ({
  bookmarkGroups,
  activeGroup,
  page,
  onSelect,
}) => {
  const { mobile } = useDeviceType();
  const {
    items,
    page: currentPage,
    prevPage,
    nextPage,
    setPage,
  } = useLazyFetchingPagination(bookmarkGroups, {
    total: page.total,
    perPage: page.perPage,
  });

  return (
    <BookmarkGroupSlider
      name={''}
      total={page.total}
      page={currentPage}
      perPage={page.perPage}
      nextPage={nextPage}
      prevPage={prevPage}
      setPage={setPage}
      showTotal={false}
      isSliderPagination={!mobile}
    >
      {items.map(bookmarkGroup => {
        return (
          <BookmarkGroupItem
            active={bookmarkGroup.id === activeGroup.id}
            key={bookmarkGroup.id}
            bookmarkGroup={bookmarkGroup}
            onSelect={onSelect}
          />
        );
      })}
    </BookmarkGroupSlider>
  );
};

export default FdrBookmarkGroupList;
